import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import PPAOnboardingHeader from 'components/PPAOnboardingHeader';
import { PATH } from 'constants/path';
import { CALENDLY_LINK } from 'constants/common';
import { trackHeapEvent } from 'utils/analytics';
import useCredentialInfo from 'hooks/credentialing/useCredentialInfo';
import useCredSetupStatus from 'hooks/credentialing/useCredSetupStatus';
import useIsExistingKlarityUser from 'hooks/credentialing/useIsExistingKlarityUser';
import { ENPIType } from 'types/credentialing';
import { getTeamMemberListPage, sendTeamMemberInvite } from 'api/group';
import type { TTeamMemberDto } from 'types/group';
import HomeScreen from './components/HomeScreen';
import ChooseProvidersScreen from './components/ChooseProvidersScreen';
import ListingPreviewScreen from './components/ListingPreviewScreen';
import EhrScreen from './components/EhrScreen';
import { isInvited, type TSubmitProvider, type TSubmitScreen } from './types';
import { getCurrentUser } from 'api/common';
import { checkLeadFormByEmail } from 'api/ppaLeadForm';
import { getInsuranceBillingStatus } from 'api/insuranceBilling';
import { getHomeInfo } from 'api/provider';
import { hasIntent, EHelpType } from 'pages/PPALeadFormPage/helpType';
import s from './s.module.less';
import { isPPAOnboardingCompleted } from 'utils/common';

// Credentialing 提交成功页 —— 结构/文案移植自设计原型 credentialing tab
// (#submitted-standard / #group-submitted / #submitted-existing / #group-submitted-existing, 09/10)
//
// 原型上的四个 demo 开关在这里对应四份真实数据，页面形态由它们组合决定：
//   TIER     Flash / Priority / Standard  → credentialInfo.slaTierDays（缺省按 slaTier 兜底 5 / 15 / 50），只影响折叠里的天数
//   FAX      Has fax / No fax             → useCredSetupStatus().faxMissing，只控制 fax 卡
//   ACCOUNT  New / Already on Klarity     → useIsExistingKlarityUser()，已是 Klarity 用户则不渲染 offer 卡与页脚切换链
//   PRACTICE Solo / Group                 → billingNpiType + providers.length，决定文案单复数与 offer 卡走 choose 还是 preview
//
// V2 的 payout 必做卡与 SpeedUpModule 在这版整体下掉：提交当天还没有理赔要回款，payout 不紧急，
// 它现在由 Credentialing status 页的 SetupReminderBanner 跟进，也仍可从 Settings > Payout account 进入。
export default function PPACredentialingSubmittedPage() {
    const navigate = useNavigate();
    const [credentialInfo] = useCredentialInfo();
    const [isExistingUser] = useIsExistingKlarityUser();

    // group（Practice NPI）提交多个 provider 时用复数文案；solo 用单数文案
    const isPracticeNpi = credentialInfo.billingNpiType?.value === ENPIType.PRACTICE_NPI;
    const isGroupSubmit = useMemo(
        () => isPracticeNpi && credentialInfo.providers.length > 1,
        [isPracticeNpi, credentialInfo.providers.length],
    );
    const providerCount = credentialInfo.providers.length;

    const { faxMissing } = useCredSetupStatus(isPracticeNpi, credentialInfo.providers);

    // 提交后处理时效：优先用接口返回的 slaTierDays；缺省时按 slaTier 档位兜底（Flash 5 / Priority 15 / Standard 50）
    const slaDays = useMemo(() => {
        const { slaTierDays, slaTier } = credentialInfo;
        if (typeof slaTierDays === 'number' && slaTierDays > 0) return slaTierDays;
        const tier = slaTier?.toUpperCase();
        if (tier === 'PRIORITY') return 15;
        if (tier === 'STANDARD') return 50;
        return 5; // FLASH / 未知档位
    }, [credentialInfo.slaTierDays, credentialInfo.slaTier]);

    // Continue onboarding（来自 feat/2026/09/lead-form-update，e38475675）：
    // 按 lead form 的 practiceHelpType 决定还差哪条 onboarding，两条意向都保留。
    // INSURANCE_BILLING 优先——它是这页唯一的 billing onboarding 入口；
    // PATIENT_GROWTH 指向的 PPA onboarding steps 和下面 offer 卡的 CTA 同一个去处，是有意的双入口。
    const [nextOnboarding, setNextOnboarding] = useState<{ label: string; path: string } | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const userRes = await getCurrentUser();
                const email = userRes?.data?.data?.actingUser?.email;
                if (!email || cancelled) return;
                const leadRes = await checkLeadFormByEmail({ email });
                const practiceHelpType = leadRes?.data?.data?.leadForm?.practiceHelpType;
                if (cancelled || !practiceHelpType) return;

                if (hasIntent(practiceHelpType, EHelpType.INSURANCE_BILLING)) {
                    const statusRes = await getInsuranceBillingStatus();
                    if (cancelled) return;
                    if (statusRes?.data?.data?.onboardingCompleted !== true) {
                        setNextOnboarding({ label: 'Continue onboarding', path: PATH.INSURANCE_BILLING_LANDING });
                        return;
                    }
                }

                if (hasIntent(practiceHelpType, EHelpType.PATIENT_GROWTH)) {
                    const homeRes = await getHomeInfo();
                    if (cancelled) return;
                    if (!isPPAOnboardingCompleted(homeRes?.data?.data)) {
                        setNextOnboarding({ label: 'Continue onboarding', path: PATH.PPA_ONBOARDING_STEPS });
                    }
                }
            } catch (e) {
                // ignore
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const [screen, setScreen] = useState<TSubmitScreen>('home');
    const [people, setPeople] = useState<TSubmitProvider[]>([]);
    const [selected, setSelected] = useState<Record<number, boolean>>({});
    const [inviting, setInviting] = useState(false);

    // choose / preview 的名单与「已邀请」状态都来自 team member 列表：
    // portalStatus 是服务端状态，所以刷新后已邀请的人仍然显示 Invited。
    const fetchPeople = useCallback(async () => {
        if (!isGroupSubmit) return;
        try {
            const res: any = await getTeamMemberListPage({ current: 1, size: 200 });
            const records: TTeamMemberDto[] = res?.data?.data?.records || [];
            const emailByProvider = new Map(credentialInfo.providers.map((p) => [p.providerId, p.email]));
            const list = records
                .filter((m) => m.providerId != null && emailByProvider.has(m.providerId as number))
                .map((m) => {
                    const first = m.firstName || '';
                    const last = m.lastName || '';
                    return {
                        providerId: m.providerId as number,
                        memberId: m.id,
                        name: `${first} ${last}`.trim(),
                        initials: `${first.charAt(0)}${last.charAt(0)}`,
                        sub: m.email || emailByProvider.get(m.providerId as number) || '',
                        invited: isInvited(m.portalStatus),
                    };
                });
            setPeople(list);
        } catch (e) {
            console.error(e);
        }
    }, [isGroupSubmit, credentialInfo.providers]);

    useEffect(() => {
        fetchPeople();
    }, [fetchPeople]);

    const invitedCount = useMemo(() => people.filter((p) => p.invited).length, [people]);

    const goTrack = () => {
        trackHeapEvent('WebApp - PPA Credentialing Submitted - Click - Track Application', {});
        navigate(`${PATH.DASHBOARD}/${PATH.CREDENTIALING}`);
    };

    const handleContinueOnboarding = () => {
        if (!nextOnboarding) return;
        trackHeapEvent('WebApp - PPA Credentialing Submitted - Click - Continue Onboarding', {});
        navigate(nextOnboarding.path);
    };

    const goScreen = (next: TSubmitScreen) => {
        trackHeapEvent('WebApp - PPA Credentialing Submitted - Click - Open Offer', { screen: next });
        setScreen(next);
    };

    const handleInvite = async () => {
        const picked = people.filter((p) => !p.invited && selected[p.providerId]);
        if (!picked.length) return;
        setInviting(true);
        try {
            const results = await Promise.all(
                picked.map(async (p) => {
                    try {
                        const res: any = await sendTeamMemberInvite(p.memberId);
                        return res?.data?.code === 200;
                    } catch (e) {
                        return false;
                    }
                }),
            );
            const sent = picked.filter((_, i) => results[i]);
            if (!sent.length) {
                message.error('Failed to send invites. Please try again.');
                return;
            }
            trackHeapEvent('WebApp - PPA Credentialing Submitted - Click - Invite Providers', { count: sent.length });
            // 停在本页：toast 在这里出现，行随之变成 Invited，owner 可以接着邀请剩下的人
            setSelected((prev) => {
                const next = { ...prev };
                sent.forEach((p) => delete next[p.providerId]);
                return next;
            });
            await fetchPeople();
            message.success(
                sent.length === 1
                    ? `Invite sent to ${sent[0].name || '1 provider'}.`
                    : `Invites sent to ${sent.length} providers.`,
            );
            if (sent.length < picked.length) {
                message.error(`${picked.length - sent.length} invite(s) could not be sent. Please try again.`);
            }
        } finally {
            setInviting(false);
        }
    };

    // preview 屏只在 solo 下出现，名单就是 provider 自己（与原型 list = people 一致）
    const previewPeople = useMemo(() => {
        const self = credentialInfo.providers[0];
        if (isGroupSubmit || !self) return [];
        const first = self.firstName || '';
        const last = self.lastName || '';
        return [
            {
                providerId: self.providerId,
                memberId: 0,
                name: `${first} ${last}`.trim(),
                initials: `${first.charAt(0)}${last.charAt(0)}`,
                sub: self.email || '',
                invited: false,
            },
        ];
    }, [isGroupSubmit, credentialInfo.providers]);

    return (
        <div className={s.page}>
            <div className={s.topbar}>
                <PPAOnboardingHeader showLogout webinarLink={CALENDLY_LINK.KLARITY_BILLING_WEBINAR} />
            </div>
            <div className={s.content}>
                {screen === 'home' && (
                    <HomeScreen
                        isGroupSubmit={isGroupSubmit}
                        providerCount={providerCount}
                        slaDays={slaDays}
                        isExistingUser={isExistingUser}
                        faxMissing={faxMissing}
                        invitedCount={invitedCount}
                        inviteTotal={people.length}
                        onTrack={goTrack}
                        onGo={goScreen}
                        continueLabel={nextOnboarding?.label}
                        onContinueOnboarding={handleContinueOnboarding}
                    />
                )}
                {screen === 'choose' && (
                    <ChooseProvidersScreen
                        people={people}
                        selected={selected}
                        inviting={inviting}
                        onBack={() => setScreen('home')}
                        onToggle={(providerId, checked) => setSelected((prev) => ({ ...prev, [providerId]: checked }))}
                        onSelectAll={(checked) => {
                            setSelected(() => {
                                const next: Record<number, boolean> = {};
                                people
                                    .filter((p) => !p.invited)
                                    .forEach((p) => {
                                        next[p.providerId] = checked;
                                    });
                                return next;
                            });
                        }}
                        onInvite={handleInvite}
                        onTrack={goTrack}
                    />
                )}
                {screen === 'preview' && (
                    <ListingPreviewScreen
                        people={previewPeople}
                        onBack={() => setScreen('home')}
                        onStartListing={() => {
                            trackHeapEvent('WebApp - PPA Credentialing Submitted - Click - Start My Listing', {});
                            navigate(PATH.PPA_ONBOARDING_STEPS);
                        }}
                        onTrack={goTrack}
                    />
                )}
                {screen === 'ehr' && (
                    <EhrScreen
                        isGroupSubmit={isGroupSubmit}
                        onBack={() => setScreen('home')}
                        onOpenEhr={() => {
                            trackHeapEvent('WebApp - PPA Credentialing Submitted - Click - Open EHR', {});
                            navigate(`${PATH.DASHBOARD}/${PATH.CLIENTS}`);
                        }}
                        onTrack={goTrack}
                    />
                )}
            </div>
        </div>
    );
}
